import {
  EPostSearchType,
  IPostSearch,
} from '@/client/legacy-shared-modules/search/search-types';
import type { MemberIndexViewableField } from '@/components/GlobalSearch/types';

import type { SearchAIResults_postsByIdsForAiAsk_postsByIdsForAiAsk_results } from '../../graphql-types/SearchAIResults_postsByIdsForAiAsk';
import {
  draftJsContentToPlainText,
  normalizeTextContent,
  orderRecordsByIds,
  pickPlainTextOrDraftContent,
} from '../../utils/contentHelpers';

function inferPostSearchType(
  post: SearchAIResults_postsByIdsForAiAsk_postsByIdsForAiAsk_results
): EPostSearchType {
  if (post.blog?._id) return EPostSearchType.BLOG;
  if (post.poll?._id) return EPostSearchType.POLL;
  if (post.question?._id) return EPostSearchType.QUESTION;
  if (post.answer?._id) return EPostSearchType.ANSWER;
  return EPostSearchType.POST;
}

function buildSharedPostHitFields(
  post: SearchAIResults_postsByIdsForAiAsk_postsByIdsForAiAsk_results,
  viewableField: MemberIndexViewableField
) {
  const author = post.author;
  const authorViewableAttributes = {
    firstName: author.firstName ?? undefined,
    lastName: author.lastName ?? undefined,
    preferredName: author.preferredName ?? undefined,
    prefix: author.prefix ?? undefined,
    suffix: author.suffix ?? undefined,
    nameTitle: author.nameTitle ?? undefined,
  };

  const datePublished = post.datePublished
    ? new Date(post.datePublished)
    : new Date(0);

  return {
    scope: [] as string[],
    objectID: post._id,
    datePublished,
    reactionCount: post.reactionCount ?? 0,
    numComments: post.commentCount ?? 0,
    profilePictureUrl: author.proxyProfilePictureUrl ?? null,
    authorId: post.authorId ?? author._id,
    displayName: author.displayName ?? '',
    group: post.group?._id
      ? { _id: post.group._id, name: post.group.name }
      : undefined,
    organization: post.organization?._id
      ? { _id: post.organization._id, name: post.organization.name }
      : undefined,
    [viewableField]: authorViewableAttributes,
  };
}

export function mapPostsByIdsToPostHits(
  posts: SearchAIResults_postsByIdsForAiAsk_postsByIdsForAiAsk_results[],
  orderedPostIds: string[],
  viewableField: MemberIndexViewableField
): IPostSearch[] {
  return orderRecordsByIds(posts, orderedPostIds).map((post) => {
    const postType = inferPostSearchType(post);
    const sharedFields = buildSharedPostHitFields(post, viewableField);

    switch (postType) {
      case EPostSearchType.BLOG:
        return {
          ...sharedFields,
          postType,
          blog: {
            _id: post.blog!._id,
            title: post.blog?.title ?? 'Article',
            published: true,
            content:
              normalizeTextContent(post.blog?.content) ||
              post.bodyPlainText?.trim() ||
              '',
          },
        } as IPostSearch;
      case EPostSearchType.POLL:
        return {
          ...sharedFields,
          postType,
          responseCount: post.poll?.responseCount ?? 0,
          poll: {
            _id: post.poll!._id,
            title: post.poll?.title ?? 'Poll',
            description:
              pickPlainTextOrDraftContent(null, post.poll?.description) ||
              draftJsContentToPlainText(post.poll?.descriptionContentState) ||
              undefined,
          },
        } as IPostSearch;
      case EPostSearchType.QUESTION:
        return {
          ...sharedFields,
          postType,
          answerCount: post.question?.answerCount ?? 0,
          question: {
            _id: post.question!._id,
            title: post.question?.title ?? 'Question',
            body:
              pickPlainTextOrDraftContent(null, post.question?.body) ||
              draftJsContentToPlainText(post.question?.bodyContentState) ||
              '',
          },
        } as IPostSearch;
      case EPostSearchType.ANSWER:
        return {
          ...sharedFields,
          postType,
          answer: {
            _id: post.answer!._id,
            body:
              pickPlainTextOrDraftContent(null, post.answer?.body) ||
              draftJsContentToPlainText(post.answer?.bodyContentState) ||
              '',
            question: {
              _id: post.answer?.question?._id ?? post.answer?.questionId ?? '',
              title: post.answer?.question?.title ?? 'Question',
            },
          },
        } as IPostSearch;
      default:
        return {
          ...sharedFields,
          postType: EPostSearchType.POST,
          title: post.title ?? 'Post',
          bodyPlainText: post.bodyPlainText ?? '',
        } as IPostSearch;
    }
  });
}
